import * as React from 'react';
import styled from 'styled-components';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { loginWithCredentials } from '../../../../core/apiWithDomainHandler';
import { LoginModel } from '../../../../models/AccountModels';
import { errorRed, greyColour, superLightGreyColour, tabletPortraitMinWidth } from '../../../cssConstants';
import EmailField from '../../../../commonComponents/appForm/EmailField';
import PasswordField from '../../../../commonComponents/appForm/PasswordField';
import { forgotPasswordPageUrl } from '../../../../core/constants';
import AppForm from '../../common/AppForm';
import { hasValue } from '../../../../lib/lib';
import { AuthenticationResult } from '../../../../models/TwoFactorAuthenticationResult';
import { ApiResultMessageModel, getAndValidateApiMessage } from '../../../../models/ApiResultMessage';
import { ApiResultMessages } from '../../../../core/enums';

const LoginForm = (props: Props) => {
	const [defaultValues, setDefaultValues] = useState({ email: '', password: '' });
	const methods = useForm({ defaultValues });
	const {
		formState: { errors }
	} = methods;

	const [showPassword, setShowPassword] = useState(false);
	const [inlineError, setInlineError] = useState<AuthenticationResult | string>(null);

	const handleSubmit = async (loginModel: LoginModel) => {
		const apiResult = await loginWithCredentials(loginModel);

		if (!apiResult.succeeded) {
			setDefaultValues(loginModel);

			const isModalError = [ApiResultMessages.AccountAwaitingApproval, ApiResultMessages.ClientAccountLocked].includes(
				apiResult.response as ApiResultMessages
			);
			if (isModalError) {
				const apiMessage = getAndValidateApiMessage(apiResult.response as string);
				props.onSubmit(loginModel.email, loginModel.password, apiMessage);
			} else {
				setInlineError(apiResult.response);
			}
		} else {
			// Successful login, proceed with 2FA
			const securityTokenResult = apiResult.response as AuthenticationResult;
			props.onSubmit(loginModel.email, loginModel.password, null, securityTokenResult);
		}
	};

	const handleTogglePasswordMask = () => {
		setShowPassword(!showPassword);
	};

	return (
		<>
			<TitleWrapper>
				<Title>Welcome</Title>
				<SubTitle>Log in to continue</SubTitle>
			</TitleWrapper>
			<AppForm methods={methods} onSubmit={handleSubmit} primaryButtonText="Log in" singleColumn>
				<EmailField
					label="Email address"
					// type="text" // TODO: Need to find another way to prevent browser automatic validation (like the email tooltip)
					required={false}
					validate={(value: string) => hasValue(value) || 'Please enter a valid email address.'}
				/>
				<PasswordField
					showPassword={showPassword}
					onTogglePasswordMask={handleTogglePasswordMask}
					required={false}
					validate={false}
					customValidate={(value: string) => hasValue(value) || 'Please enter a valid password.'}
				/>
				{inlineError && Object.keys(errors).length === 0 && <InlineError>{inlineError}</InlineError>}
				<RightAlignedLink to={forgotPasswordPageUrl}>Forgot your password?</RightAlignedLink>
			</AppForm>
		</>
	);
};

export default LoginForm;

interface Props {
	onSubmit: (
		email: string,
		password: string,
		apiMessage?: ApiResultMessageModel,
		authenticationResult?: AuthenticationResult
	) => void;
}

export const TitleWrapper = styled.div`
	padding-top: 44px;
`;

export const Title = styled.p`
	font-size: 24px;
	font-weight: 400;
	line-height: 32px;
	color: ${(props) => props.theme.brandingColourHeavy};

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 28px;
	}
`;

export const SubTitle = styled.p`
	font-size: 14px;
	font-weight: 400;
	line-height: 20px;
	color: ${superLightGreyColour};

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 16px;
		margin-top: 10px;
	}
`;

export const InlineError = styled.p`
	font-size: 12px;
	font-weight: 500;
	margin: 5px 0 20px 0;
	color: ${errorRed};

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 14px;
	}
`;

const RightAlignedLink = styled(Link)`
	text-align: left;
	text-decoration: none;
	display: block;
	margin: 5px auto;
	font-size: 14px;
	font-weight: 500;
	color: ${greyColour};

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 16px;
	}
`;
